import { ethers } from 'ethers';
import { CONTRACT_ADDRESS, CONTRACT_ABI, USDT_ADDRESS, USDT_ABI } from '../config/contract';
import { CURRENT_NETWORK } from '../config/network';

export const getProvider = () => {
  if (window.ethereum) {
    return new ethers.providers.Web3Provider(window.ethereum);
  }
  return null;
};

export const getSigner = () => {
  const provider = getProvider();
  return provider ? provider.getSigner() : null;
};

export const getContract = () => {
  const signer = getSigner();
  return signer ? new ethers.Contract(CONTRACT_ADDRESS, CONTRACT_ABI, signer) : null;
};

export const getContractReadOnly = () => {
  const provider = getProvider();
  return provider ? new ethers.Contract(CONTRACT_ADDRESS, CONTRACT_ABI, provider) : null;
};

export const getUSDTContract = () => {
  const signer = getSigner();
  return signer ? new ethers.Contract(USDT_ADDRESS, USDT_ABI, signer) : null;
};

export const getUSDTContractReadOnly = () => {
  const provider = getProvider();
  return provider ? new ethers.Contract(USDT_ADDRESS, USDT_ABI, provider) : null;
};

export const connectWallet = async () => {
  if (!window.ethereum) {
    throw new Error('No wallet detected. Use Dapp Browser to connect from Safepal or Trust Wallet');
  }
  try {
    const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
    await switchToCorrectNetwork();
    return accounts[0];
  } catch (error) {
    console.error('Error connecting wallet:', error);
    throw error;
  }
};

export const switchToCorrectNetwork = async () => {
  if (!window.ethereum) return;
  try {
    const currentChainId = await window.ethereum.request({ method: 'eth_chainId' });
    if (currentChainId !== CURRENT_NETWORK.chainId) {
      try {
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: CURRENT_NETWORK.chainId }],
        });
      } catch (switchError) {
        if (switchError.code === 4902) {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: CURRENT_NETWORK.chainId,
              chainName: CURRENT_NETWORK.chainName,
              nativeCurrency: CURRENT_NETWORK.nativeCurrency,
              rpcUrls: CURRENT_NETWORK.rpcUrls,
              blockExplorerUrls: CURRENT_NETWORK.blockExplorerUrls,
            }],
          });
        } else {
          throw switchError;
        }
      }
    }
  } catch (error) {
    console.error('Error switching network:', error);
    throw error;
  }
};

export const listenToAccountChanges = (callback) => {
  if (window.ethereum) {
    window.ethereum.on('accountsChanged', (accounts) => {
      callback(accounts.length > 0 ? accounts[0] : null);
    });
  }
};

export const listenToChainChanges = (callback) => {
  if (window.ethereum) {
    window.ethereum.on('chainChanged', callback);
  }
};

export const getBalance = async (address) => {
  const provider = getProvider();
  if (provider && address) {
    const balance = await provider.getBalance(address);
    return ethers.utils.formatEther(balance);
  }
  return '0';
};

export const getUSDTBalance = async (address) => {
  const usdtContract = getUSDTContractReadOnly();
  if (usdtContract && address) {
    const balance = await usdtContract.balanceOf(address);
    return ethers.utils.formatUnits(balance, 18);
  }
  return '0';
};

export const approveUSDT = async (amount) => {
  const usdtContract = getUSDTContract();
  if (usdtContract) {
    const amountInWei = ethers.utils.parseUnits(amount.toString(), 18);
    const tx = await usdtContract.approve(CONTRACT_ADDRESS, amountInWei);
    await tx.wait();
    return tx;
  }
  throw new Error('USDT contract not available');
};

export const checkUSDTAllowance = async (address) => {
  const usdtContract = getUSDTContractReadOnly();
  if (usdtContract && address) {
    const allowance = await usdtContract.allowance(address, CONTRACT_ADDRESS);
    return ethers.utils.formatUnits(allowance, 18);
  }
  return '0';
};
